<script setup>
import { ref, computed, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Auth from '../../../../api/auth';
import AgentAPI from '../../../../api/agents';
import wootConstants from 'dashboard/constants/globals';
import {
  HIDEABLE_SIDEBAR_ITEMS,
  sidebarItemKey,
} from 'dashboard/constants/sidebarItems';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  disabledSidebarItems: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);
const agentCredentials = ref({ email: props.email });

// The conversation view the agent gets in each inbox they collaborate on:
// the standard assignment tabs or a single WhatsApp-style list.
const inboxViews = ref([]);
const inboxViewsLoaded = ref(false);
let savedInboxViews = new Map();

// The inboxes the agent collaborates on, edited here so an agent can be
// added to many inboxes at once instead of one inbox settings page at a time.
const selectedInboxIds = ref([]);
const inboxesLoaded = ref(false);
let savedInboxIds = [];

onMounted(async () => {
  try {
    const [{ data: viewsData }, { data: inboxesData }] = await Promise.all([
      AgentAPI.getInboxViews(props.id),
      AgentAPI.getInboxes(props.id),
    ]);
    inboxViews.value = viewsData.payload;
    savedInboxViews = new Map(
      viewsData.payload.map(view => [view.inbox_id, view.conversation_view])
    );
    selectedInboxIds.value = [...inboxesData.payload];
    savedInboxIds = inboxesData.payload;
  } finally {
    inboxViewsLoaded.value = true;
    inboxesLoaded.value = true;
  }
});

// Sidebar entries hidden for this agent, by key. Stored as the hidden ones so
// entries added by a Chatwoot update show up by default.
const hiddenSidebarItems = ref([...props.disabledSidebarItems]);

const isSidebarItemHidden = key => hiddenSidebarItems.value.includes(key);

const toggleSidebarItem = key => {
  hiddenSidebarItems.value = isSidebarItemHidden(key)
    ? hiddenSidebarItems.value.filter(item => item !== key)
    : [...hiddenSidebarItems.value, key];
};

const isInboxSelected = inboxId => selectedInboxIds.value.includes(inboxId);

const toggleInbox = inboxId => {
  selectedInboxIds.value = isInboxSelected(inboxId)
    ? selectedInboxIds.value.filter(id => id !== inboxId)
    : [...selectedInboxIds.value, inboxId];
};

const inboxesChanged = () =>
  selectedInboxIds.value.length !== savedInboxIds.length ||
  selectedInboxIds.value.some(id => !savedInboxIds.includes(id));

const changedInboxViews = () =>
  inboxViews.value.filter(
    view => savedInboxViews.get(view.inbox_id) !== view.conversation_view
  );

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const inboxes = useMapGetter('inboxes/getInboxes');

const allInboxesSelected = computed(
  () =>
    inboxes.value.length > 0 &&
    inboxes.value.every(inbox => isInboxSelected(inbox.id))
);

const toggleAllInboxes = () => {
  selectedInboxIds.value = allInboxesSelected.value
    ? []
    : inboxes.value.map(inbox => inbox.id);
};
const getCustomRoles = useMapGetter('customRole/getCustomRoles');
const currentUserId = useMapGetter('getCurrentUserID');

// Editing yourself is allowed for the per-inbox conversation view, but the
// role stays locked so the only administrator cannot demote themselves.
const isCurrentUser = computed(() => props.id === currentUserId.value);

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

// Administrators always keep Settings, so the checkbox is shown locked for
// them.
const isAdministratorRole = computed(
  () => selectedRole.value?.name === 'administrator'
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
      disabled_sidebar_items: hiddenSidebarItems.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);
    // The sidebar reads the hidden entries from the signed-in profile.
    if (isCurrentUser.value) await store.dispatch('setUser');

    if (inboxesChanged()) {
      await AgentAPI.updateInboxes(props.id, selectedInboxIds.value);
    }

    // A view lives on the collaborator link, so for non-administrators a view
    // on an inbox they were just removed from has nowhere to be stored.
    const changedViews = changedInboxViews().filter(
      view => isAdministratorRole.value || isInboxSelected(view.inbox_id)
    );
    if (changedViews.length) {
      await AgentAPI.updateInboxViews(props.id, changedViews);
      // The chat list reads the mode from the inboxes store, so refresh it
      // for the change to apply without a full reload.
      if (isCurrentUser.value) await store.dispatch('inboxes/get');
    }

    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select
            v-model="selectedRoleId"
            :disabled="isCurrentUser"
            @change="v$.selectedRoleId.$touch"
          >
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="isCurrentUser" class="text-xs text-n-slate-11">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.SELF_LOCKED') }}
          </span>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <div v-if="inboxesLoaded" class="w-full mb-4">
        <label>{{ $t('AGENT_MGMT.EDIT.FORM.INBOXES.LABEL') }}</label>
        <p class="mt-1 mb-2 text-sm text-n-slate-11">
          {{ $t('AGENT_MGMT.EDIT.FORM.INBOXES.HINT') }}
        </p>
        <p v-if="!inboxes.length" class="mb-0 text-sm text-n-slate-11">
          {{ $t('AGENT_MGMT.EDIT.FORM.INBOXES.EMPTY') }}
        </p>
        <div v-else class="flex flex-col gap-1.5">
          <label
            class="flex items-center gap-2 !mb-0 text-sm font-medium text-n-slate-12"
          >
            <input
              type="checkbox"
              class="!m-0"
              :checked="allInboxesSelected"
              @change="toggleAllInboxes"
            />
            {{ $t('AGENT_MGMT.EDIT.FORM.INBOXES.SELECT_ALL') }}
          </label>
          <div class="flex flex-col gap-1.5 overflow-y-auto max-h-48">
            <label
              v-for="inbox in inboxes"
              :key="inbox.id"
              class="flex items-center gap-2 !mb-0 text-sm font-normal ltr:pl-6 rtl:pr-6 text-n-slate-12"
            >
              <input
                type="checkbox"
                class="!m-0"
                :checked="isInboxSelected(inbox.id)"
                @change="toggleInbox(inbox.id)"
              />
              <span class="truncate">{{ inbox.name }}</span>
            </label>
          </div>
        </div>
      </div>

      <div v-if="inboxViewsLoaded" class="w-full mb-4">
        <label>
          {{ $t('AGENT_MGMT.EDIT.FORM.CONVERSATION_VIEW.LABEL') }}
        </label>
        <p v-if="!inboxViews.length" class="mt-1 mb-0 text-sm text-n-slate-11">
          {{ $t('AGENT_MGMT.EDIT.FORM.CONVERSATION_VIEW.EMPTY') }}
        </p>
        <div v-else class="flex flex-col gap-2 mt-1">
          <div
            v-for="view in inboxViews"
            :key="view.inbox_id"
            class="grid grid-cols-2 items-center gap-2"
          >
            <span class="text-sm truncate text-n-slate-12">
              {{ view.inbox_name }}
            </span>
            <select v-model="view.conversation_view" class="!mb-0">
              <option value="standard">
                {{ $t('AGENT_MGMT.EDIT.FORM.CONVERSATION_VIEW.STANDARD') }}
              </option>
              <option value="whatsapp">
                {{ $t('AGENT_MGMT.EDIT.FORM.CONVERSATION_VIEW.WHATSAPP') }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="w-full mb-4">
        <label>{{ $t('AGENT_MGMT.EDIT.FORM.SIDEBAR_ITEMS.LABEL') }}</label>
        <p class="mt-1 mb-2 text-sm text-n-slate-11">
          {{ $t('AGENT_MGMT.EDIT.FORM.SIDEBAR_ITEMS.HINT') }}
        </p>
        <div class="flex flex-col gap-1.5">
          <template v-for="item in HIDEABLE_SIDEBAR_ITEMS" :key="item.name">
            <label
              class="flex items-center gap-2 !mb-0 text-sm font-medium text-n-slate-12"
            >
              <input
                type="checkbox"
                class="!m-0"
                :checked="
                  !isSidebarItemHidden(item.name) ||
                  (item.name === 'Settings' && isAdministratorRole)
                "
                :disabled="item.name === 'Settings' && isAdministratorRole"
                @change="toggleSidebarItem(item.name)"
              />
              {{ $t(item.label) }}
            </label>
            <label
              v-for="child in item.children"
              :key="sidebarItemKey(child.name, item.name)"
              class="flex items-center gap-2 !mb-0 text-sm font-normal ltr:pl-6 rtl:pr-6 text-n-slate-11"
            >
              <input
                type="checkbox"
                class="!m-0"
                :checked="
                  !isSidebarItemHidden(sidebarItemKey(child.name, item.name))
                "
                :disabled="isSidebarItemHidden(item.name)"
                @change="
                  toggleSidebarItem(sidebarItemKey(child.name, item.name))
                "
              />
              {{ $t(child.label) }}
            </label>
          </template>
        </div>
        <span v-if="isAdministratorRole" class="text-xs text-n-slate-11">
          {{ $t('AGENT_MGMT.EDIT.FORM.SIDEBAR_ITEMS.ADMIN_SETTINGS') }}
        </span>
      </div>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
